import React, { useState } from "react";
import { useToast } from "../../hooks/useToast";

// ─── Feature row ───────────────────────────────────────────────────────────────
function Feature({ text, included = true }) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        fontSize: "0.9rem",
        color: included ? "var(--text, #1C1C1E)" : "var(--muted, #9ca3af)",
        marginBottom: "8px",
        listStyle: "none",
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontWeight: 700,
          color: included ? "#22C55E" : "var(--muted, #9ca3af)",
          fontSize: "1rem",
          lineHeight: 1.3,
        }}
      >
        {included ? "✓" : "✗"}
      </span>
      {text}
    </li>
  );
}

// ─── FAQ accordion item ────────────────────────────────────────────────────────
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: "1px solid var(--border, #e5e7eb)",
        padding: "0",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          background: "none",
          border: "none",
          padding: "18px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          textAlign: "left",
          fontSize: "0.95rem",
          fontWeight: 600,
          color: "var(--text, #1C1C1E)",
          gap: "12px",
        }}
      >
        {q}
        <span
          style={{
            flexShrink: 0,
            fontSize: "1.25rem",
            color: "var(--muted, #6b7280)",
            lineHeight: 1,
            transform: open ? "rotate(45deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          +
        </span>
      </button>
      {open && (
        <p
          style={{
            margin: "0 0 18px",
            fontSize: "0.9rem",
            color: "var(--muted, #6b7280)",
            lineHeight: 1.6,
          }}
        >
          {a}
        </p>
      )}
    </div>
  );
}

// ─── Pricing card ──────────────────────────────────────────────────────────────
function PlanCard({ badge, badgeHighlight, title, price, priceNote, comparison, features, ctaLabel, ctaDisabled, ctaOutline, onCta, highlight }) {
  return (
    <div
      style={{
        border: highlight ? "2px solid var(--theme-accent)" : "1px solid var(--border, #e5e7eb)",
        borderRadius: "20px",
        padding: "28px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "0",
        position: "relative",
        boxShadow: highlight ? "0 4px 24px var(--theme-accent-light)" : "none",
        background: highlight ? "var(--theme-accent-soft)" : "var(--surface, #fff)",
      }}
    >
      {/* Badge */}
      <div style={{ marginBottom: "16px" }}>
        <span
          style={{
            display: "inline-block",
            background: badgeHighlight ? "var(--theme-accent)" : "var(--surface-alt, #f3f4f6)",
            color: badgeHighlight ? "#fff" : "var(--muted, #6b7280)",
            borderRadius: "999px",
            padding: "4px 12px",
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          {badge}
        </span>
      </div>

      {/* Title */}
      <h3 style={{ margin: "0 0 12px", fontSize: "1.2rem", fontWeight: 800, color: "var(--text, #1C1C1E)" }}>
        {title}
      </h3>

      {/* Price */}
      <div style={{ marginBottom: "4px" }}>
        <span style={{ fontSize: "2.2rem", fontWeight: 900, color: "var(--text, #1C1C1E)", lineHeight: 1 }}>
          {price}
        </span>
        {priceNote && (
          <span style={{ fontSize: "0.85rem", color: "var(--muted, #6b7280)", marginLeft: "4px" }}>
            {priceNote}
          </span>
        )}
      </div>

      {comparison && (
        <p style={{ margin: "0 0 20px", fontSize: "0.75rem", color: "var(--muted, #9ca3af)", lineHeight: 1.4 }}>
          {comparison}
        </p>
      )}
      {!comparison && <div style={{ marginBottom: "20px" }} />}

      {/* Features */}
      <ul style={{ margin: "0 0 24px", padding: 0, flex: 1 }}>
        {features.map((f, i) => (
          <Feature key={i} text={f.text} included={f.included !== false} />
        ))}
      </ul>

      {/* CTA */}
      <button
        type="button"
        onClick={onCta}
        disabled={ctaDisabled}
        style={{
          width: "100%",
          padding: "15px 20px",
          borderRadius: "12px",
          fontSize: "0.95rem",
          fontWeight: 700,
          cursor: ctaDisabled ? "not-allowed" : "pointer",
          border: ctaOutline ? "2px solid var(--border, #e5e7eb)" : "none",
          background: ctaDisabled
            ? "var(--surface-alt, #f3f4f6)"
            : ctaOutline
            ? "transparent"
            : "var(--theme-accent)",
          color: ctaDisabled
            ? "var(--muted, #9ca3af)"
            : ctaOutline
            ? "var(--text, #1C1C1E)"
            : "#fff",
          transition: "opacity 0.15s",
          opacity: ctaDisabled ? 0.6 : 1,
        }}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

// ─── SubscriptionPage ──────────────────────────────────────────────────────────
export default function SubscriptionPage({ subscription, onClose, onUpgrade }) {
  const { showToast } = useToast();

  function handleUpgrade(tierId) {
    if (onUpgrade) {
      onUpgrade(tierId);
    } else {
      showToast("Stripe payments coming soon — you'll be first to know!", "info");
    }
  }

  const currentTierId = subscription?.tier?.id || "free";

  const plans = [
    {
      tierId: "free",
      badge: "Get started",
      badgeHighlight: false,
      title: "Tradie",
      price: "Free",
      priceNote: null,
      comparison: null,
      features: [
        { text: "Up to 5 jobs per month" },
        { text: "Direct quote link" },
        { text: "Customer chat" },
        { text: "PDF quotes" },
        { text: "Lead marketplace access" },
        { text: "No free credits — purchase separately" },
      ],
      ctaLabel: currentTierId === "free" ? "Current plan" : "Start free",
      ctaDisabled: currentTierId === "free",
      ctaOutline: true,
      highlight: false,
    },
    {
      tierId: "sole_trader",
      badge: "Most popular for solo tradies",
      badgeHighlight: false,
      title: "Pro",
      price: "$29",
      priceNote: "/month",
      comparison: "Less than $1 a day · Cheaper than ServiceM8 ($29–$349/mo) and Tradify ($48/mo)",
      features: [
        { text: "Up to 20 jobs per month" },
        { text: "200 free marketplace credits per month" },
        { text: "Lead marketplace access" },
        { text: "Direct quote link" },
        { text: "Customer chat + file sharing" },
        { text: "PDF quotes & invoices" },
        { text: "Job calendar & booking" },
      ],
      ctaLabel: currentTierId === "sole_trader" ? "Current plan" : "Start 14-day free trial",
      ctaDisabled: currentTierId === "sole_trader",
      ctaOutline: false,
      highlight: false,
    },
    {
      tierId: "professional",
      badge: "Most Popular",
      badgeHighlight: true,
      title: "Ultimate",
      price: "$99",
      priceNote: "/month",
      comparison: "Less than a coffee a day",
      features: [
        { text: "Unlimited jobs per month" },
        { text: "1,000 free marketplace credits per month" },
        { text: "Priority lead matching" },
        { text: "Advanced quote templates" },
        { text: "Business analytics dashboard" },
        { text: "Customer review management" },
      ],
      ctaLabel: currentTierId === "professional" ? "Current plan" : "Start 14-day free trial",
      ctaDisabled: currentTierId === "professional",
      ctaOutline: false,
      highlight: true,
    },
    {
      tierId: "enterprise",
      badge: "For growing businesses",
      badgeHighlight: false,
      title: "Enterprise",
      price: "Let's talk",
      priceNote: null,
      comparison: "Custom pricing for your team",
      features: [
        { text: "Everything in Ultimate" },
        { text: "Multiple users" },
        { text: "Custom credit packages" },
        { text: "Dedicated account manager" },
        { text: "Priority support" },
        { text: "Custom integrations" },
      ],
      ctaLabel: "Contact us",
      ctaDisabled: false,
      ctaOutline: true,
      highlight: false,
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "var(--bg, #f9fafb)",
        overflowY: "auto",
      }}
    >
      {/* ── Close / back bar ── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "var(--bg, #f9fafb)",
          borderBottom: "1px solid var(--border, #e5e7eb)",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "var(--muted, #6b7280)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "0",
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "1px solid var(--border, #e5e7eb)",
            borderRadius: "8px",
            padding: "6px 14px",
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "var(--muted, #6b7280)",
            cursor: "pointer",
          }}
        >
          Maybe later
        </button>
      </div>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 20px 64px" }}>

        {/* ── Hero header ── */}
        {/* Use the shared --dark-panel-bg token so this hero lifts to the
            elevated dark surface (#2C2C2E) in dark mode. Hardcoded #1C1C1E
            previously rendered as dark-on-near-black, making the whole card
            almost invisible. */}
        <div
          style={{
            background: "var(--dark-panel-bg, #1C1C1E)",
            borderRadius: "20px",
            padding: "40px 32px",
            marginBottom: "40px",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              margin: "0 0 12px",
              fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
              fontWeight: 900,
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            Simple pricing. No surprises.
          </h1>
          <p style={{ margin: 0, fontSize: "1rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
            Cancel anytime. No lock-in contracts. Start free — no card needed.
          </p>
        </div>

        {/* ── Pricing cards ── */}
        <style>{`
          @media (min-width: 640px) {
            .rqp-pricing-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (min-width: 1024px) {
            .rqp-pricing-grid { grid-template-columns: repeat(4, 1fr) !important; }
          }
        `}</style>
        <div
          className="rqp-pricing-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "16px",
            marginBottom: "48px",
            alignItems: "start",
          }}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.tierId}
              {...plan}
              onCta={() => {
                if (plan.tierId === "enterprise") {
                  window.location.href = "mailto:hello@remotequote.io";
                } else {
                  handleUpgrade(plan.tierId);
                }
              }}
            />
          ))}
        </div>

        {/* ── Trust strip ── */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "24px",
            marginBottom: "56px",
            padding: "24px",
            background: "var(--surface, #fff)",
            borderRadius: "16px",
            border: "1px solid var(--border, #e5e7eb)",
          }}
        >
          {[
            { icon: "🔒", text: "No credit card required to start" },
            { icon: "✓",  text: "Cancel anytime — no lock-in" },
            { icon: "🦘", text: "Australian-built for Australian tradies" },
          ].map(({ icon, node, text }) => (
            <div
              key={text}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "var(--text, #1C1C1E)",
              }}
            >
              {node ?? <span style={{ fontSize: "1.1rem" }}>{icon}</span>}
              {text}
            </div>
          ))}
        </div>

        {/* ── FAQ ── */}
        <div style={{ maxWidth: "680px", margin: "0 auto" }}>
          <h2
            style={{
              margin: "0 0 8px",
              fontSize: "1.4rem",
              fontWeight: 800,
              color: "var(--text, #1C1C1E)",
              textAlign: "center",
            }}
          >
            Common questions
          </h2>
          <p style={{ textAlign: "center", color: "var(--muted, #6b7280)", margin: "0 0 32px", fontSize: "0.9rem" }}>
            Plain answers, no fluff.
          </p>

          <FaqItem
            q="What happens when my free trial ends?"
            a="You'll drop to the free plan automatically — no charges, no surprises. You can keep quoting and invoicing up to 3 jobs. Upgrade whenever you're ready."
          />
          <FaqItem
            q="What's a lead credit?"
            a="Each credit lets you unlock one job request from a customer in your area. You only pay for the leads you want — no monthly subscription required for extra credits."
          />
          <FaqItem
            q="How is this different from ServiceM8 or Tradify?"
            a="Those apps help you manage jobs — but they don't bring you new customers. Remote Quote does both. You get the same quoting and invoicing tools, plus a built-in lead marketplace that sends real job requests from customers in your area directly to your dashboard."
          />
        </div>

      </div>
    </div>
  );
}
